import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changelogApi } from '../lib/api';

export type ChangelogStatus = 'DRAFT' | 'PUBLISHED';

export interface ChangelogEntry {
  id: string;
  title: string;
  slug: string;
  body: string;
  tags: string[];
  version: string | null;
  status: ChangelogStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChangelogListResponse {
  data: ChangelogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateChangelogPayload {
  title: string;
  body: string;
  tags?: string[];
  version?: string;
  status?: ChangelogStatus;
}

export interface UpdateChangelogPayload {
  title?: string;
  body?: string;
  tags?: string[];
  version?: string;
  status?: ChangelogStatus;
}

export function useChangelogAdmin(params?: { limit?: number; offset?: number; status?: string }) {
  return useQuery<ChangelogListResponse>({
    queryKey: ['changelog', 'admin', params],
    queryFn: async () => {
      const { data } = await changelogApi.get('/admin', { params });
      return data;
    },
  });
}


export function useCreateChangelog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateChangelogPayload) => {
      const { data } = await changelogApi.post('', payload);
      return data as ChangelogEntry;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['changelog'] }),
  });
}

export function useUpdateChangelog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateChangelogPayload }) => {
      const { data: result } = await changelogApi.patch(`/${id}`, data);
      return result as ChangelogEntry;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['changelog'] }),
  });
}

export function usePublishChangelog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await changelogApi.post(`/${id}/publish`);
      return data as ChangelogEntry;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['changelog'] }),
  });
}

export function useUnpublishChangelog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await changelogApi.post(`/${id}/unpublish`);
      return data as ChangelogEntry;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['changelog'] }),
  });
}

export function useDeleteChangelog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await changelogApi.delete(`/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['changelog'] }),
  });
}
